import { useCallback, useEffect, useState } from 'react';
import { RequireBot } from '../components/ChooseBot';
import { useBot } from '../contexts/BotContext';
import {
  hubClient,
  type CreateHookDto,
  type Hook,
  type HookMeta,
  type HookType,
} from '../services/hubClient';

const inputCls = 'w-full rounded border border-gray-300 px-3 py-2 text-sm';
const labelCls = 'block text-xs font-medium text-gray-500 mb-1';

interface DraftState {
  id?: string;
  name: string;
  event: string;
  type: HookType;
  configText: string;
  priority: number;
  timeoutMs: number;
  enabled: boolean;
  description: string;
}

function emptyDraft(event: string): DraftState {
  return { name: '', event, type: 'webhook', configText: '{\n  "url": ""\n}', priority: 100, timeoutMs: 1500, enabled: true, description: '' };
}

function HooksPage() {
  const { system, botId } = useBot();
  const [meta, setMeta] = useState<HookMeta | null>(null);
  const [hooks, setHooks] = useState<Hook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [m, h] = await Promise.all([
        meta ? Promise.resolve(meta) : hubClient.getHookMeta(),
        hubClient.listHooks(system, botId),
      ]);
      setMeta(m);
      setHooks(h);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load hooks');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [system, botId]);

  useEffect(() => {
    void load();
  }, [load]);

  function startEdit(h: Hook) {
    setDraft({
      id: h.id,
      name: h.name,
      event: h.event,
      type: h.type,
      configText: JSON.stringify(h.config ?? {}, null, 2),
      priority: h.priority,
      timeoutMs: h.timeoutMs,
      enabled: h.enabled,
      description: h.description ?? '',
    });
  }

  async function toggleEnabled(h: Hook) {
    try {
      await hubClient.updateHook(h.id, { enabled: !h.enabled });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to toggle');
    }
  }

  async function save() {
    if (!draft) return;
    let config: Record<string, unknown>;
    try {
      config = draft.configText.trim() ? JSON.parse(draft.configText) : {};
      if (typeof config !== 'object' || config === null || Array.isArray(config)) throw new Error('config must be a JSON object');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'config is not valid JSON');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (draft.id) {
        await hubClient.updateHook(draft.id, {
          name: draft.name.trim(),
          event: draft.event,
          type: draft.type,
          config,
          priority: draft.priority,
          timeoutMs: draft.timeoutMs,
          enabled: draft.enabled,
          description: draft.description.trim() || undefined,
        });
      } else {
        const dto: CreateHookDto = {
          system,
          botId,
          name: draft.name.trim(),
          event: draft.event,
          type: draft.type,
          config,
          priority: draft.priority,
          timeoutMs: draft.timeoutMs,
          enabled: draft.enabled,
          description: draft.description.trim() || undefined,
        };
        await hubClient.createHook(dto);
      }
      setDraft(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save hook');
    } finally {
      setSaving(false);
    }
  }

  async function remove(h: Hook) {
    if (!confirm(`Delete hook "${h.name}"?`)) return;
    try {
      await hubClient.deleteHook(h.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete');
    }
  }

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-50">
      <div className="mx-auto max-w-5xl px-6 py-8">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Hooks</h1>
            <p className="text-gray-600">
              Lifecycle hooks for {system}/{botId} — webhook callbacks or built-in handlers on pre/post events
              (deny-first, timeout-bounded). Lower priority runs first.
            </p>
          </div>
          <button onClick={() => setDraft(emptyDraft(meta?.events[0] ?? 'pre.route'))} className="rounded bg-blue-600 px-4 py-2 text-sm text-white">
            New hook
          </button>
        </div>

        {error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        <div className="mb-6 overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['Name', 'Event', 'Type', 'Prio', 'On', ''].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {hooks.map((h) => (
                <tr key={h.id}>
                  <td className="px-3 py-2 font-medium text-gray-900">{h.name}</td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-600">{h.event}</td>
                  <td className="px-3 py-2 text-xs">{h.type}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{h.priority}</td>
                  <td className="px-3 py-2">
                    <button onClick={() => toggleEnabled(h)} className={`rounded px-2 py-0.5 text-[11px] ${h.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                      {h.enabled ? 'enabled' : 'off'}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => startEdit(h)} className="rounded px-2 py-1 text-sm text-blue-600 hover:bg-blue-50">Edit</button>
                    <button onClick={() => remove(h)} className="rounded px-2 py-1 text-sm text-gray-400 hover:bg-red-50 hover:text-red-600">Delete</button>
                  </td>
                </tr>
              ))}
              {!loading && hooks.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-sm text-gray-400">No hooks for this bot.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {draft && (
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="mb-3 text-sm font-semibold">{draft.id ? `Edit "${draft.name}"` : 'New hook'}</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Name</label>
                <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Event</label>
                <select value={draft.event} onChange={(e) => setDraft({ ...draft, event: e.target.value })} className={inputCls}>
                  {(meta?.events ?? [draft.event]).map((ev) => (
                    <option key={ev} value={ev}>{ev}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Type</label>
                <select value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value as HookType })} className={inputCls}>
                  <option value="webhook">webhook</option>
                  <option value="builtin">builtin</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelCls}>Priority</label>
                  <input type="number" value={draft.priority} onChange={(e) => setDraft({ ...draft, priority: Number(e.target.value) })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Timeout ms</label>
                  <input type="number" value={draft.timeoutMs} onChange={(e) => setDraft({ ...draft, timeoutMs: Number(e.target.value) })} className={inputCls} />
                </div>
              </div>
              <div className="col-span-2">
                <label className={labelCls}>
                  Config (JSON) — {draft.type === 'webhook' ? '{ "url", "headers"?, "secret"? }' : `{ "handler" } · available: ${(meta?.builtinHandlers ?? []).join(', ') || 'none'}`}
                </label>
                <textarea value={draft.configText} onChange={(e) => setDraft({ ...draft, configText: e.target.value })} className={`${inputCls} font-mono`} rows={5} />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Description (optional)</label>
                <input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className={inputCls} />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
                Enabled
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setDraft(null)} className="rounded px-3 py-1.5 text-sm text-gray-600">Cancel</button>
              <button onClick={save} disabled={saving || !draft.name.trim()} className="rounded bg-blue-600 px-4 py-1.5 text-sm text-white disabled:opacity-40">
                {saving ? 'Saving…' : 'Save hook'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Hooks;

/** A1: renders only once a bot is chosen (no default bot). */
export function Hooks() {
  return (
    <RequireBot title="Hooks">
      <HooksPage />
    </RequireBot>
  );
}
